import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
  AddOrganisation,
  EditOrganisation,
  Id,
  OrganisationView,
  OrganisationService as ApiOrganisationService,
  OrganisationSearchParameters,
} from '@src/api';
import { OrganisationResponseUI, OrganisationUI, OrganisationUpsert } from '@src/models';
import { AlertService, BaseSearchService, UserService } from '@src/core/services';
import { CustomNamesService } from '@src/app/modules/custom-name-tabs';
import { OrganisationInfoStoreService } from '@src/app/modules/organisations';

import { APP_CONFIG } from '../config';

@Injectable({
  providedIn: 'root',
})
export class OrganisationService
  implements BaseSearchService<OrganisationSearchParameters, any, OrganisationResponseUI>
{
  private readonly organisationService = inject(ApiOrganisationService);
  private readonly userService = inject(UserService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly customNamesService = inject(CustomNamesService);
  private readonly organisationInfoStore = inject(OrganisationInfoStoreService);

  currentParentOrganisation$ = new BehaviorSubject<OrganisationView | undefined>(undefined);
  parentOrganisations$ = new BehaviorSubject<OrganisationUI[]>([{}]);
  childOrganisationList$ = new BehaviorSubject<OrganisationView[]>([{}]);
  organisationList$ = new BehaviorSubject<OrganisationView[] | null>(null);

  filter$ = new BehaviorSubject<OrganisationSearchParameters | null>(null);
  sort$ = new BehaviorSubject<null>(null);

  pagination$ = new BehaviorSubject<number>(0);

  getOrganisationNew(id: string, loadObjectMenu?: boolean): Observable<OrganisationUI | null> {
    return this.organisationService.getOrganisation(id).pipe(
      catchError(() => {
        this.alertService.error(
          this.translateService.instant('components.organisation.alerts.errors.organisationNotFound'),
        );
        return of(null);
      }),
      map(organisation => {
        if (id && loadObjectMenu) {
          this.customNamesService.getObjectMenuWCustom(id);
        }

        return organisation ? this.sortingOrganisationContacts(organisation) : null;
      }),
      map(organisation => (organisation ? this.normalizeOrganisationDataForUI(organisation) : null)),
      tap(organisation => {
        if (organisation?.id) {
          this.organisationInfoStore.setData(organisation.id, organisation);
        }
      }),
    );
  }

  getOrganisationList(top: number = 1000, start: number = 0): void {
    this.organisationService
      .organisationList(top, start)
      .pipe(
        catchError(err => {
          this.alertService.error(err, { autoClose: 30000 });
          return throwError(() => err);
        }),
        map(organisationList => this.sortingOrganisations(organisationList)),
      )
      .subscribe(organisationList => this.organisationList$.next(organisationList));
  }

  // TODO: возможно можно объединить в методом getOrganisation
  getOrganisationData(id: string): Promise<OrganisationView> {
    return lastValueFrom(this.organisationService.getOrganisation(id)).then();
  }

  getParentOrganisation(all: boolean): void {
    this.organisationService
      .getParentOrganisation(all)
      .pipe(
        catchError(err => {
          this.alertService.error(err, { autoClose: 30000 });
          return throwError(() => err);
        }),
      )
      .subscribe(organisations => {
        if (all) {
          this.parentOrganisations$.next(organisations);
          const currentParentOrganisation = organisations.find(
            organisation => organisation.id && organisation.id === this.userService.authUser?.parentOrganisationId,
          );
          this.currentParentOrganisation$.next(currentParentOrganisation);
        } else {
          this.currentParentOrganisation$.next(organisations?.[0]);
        }
      });
  }

  getChildOrganisationList(id: string): void {
    this.organisationService
      .getChildOrganisationList(id)
      .pipe(
        catchError(err => {
          this.alertService.error(err, { autoClose: 30000 });
          return throwError(() => err);
        }),
        map(childOrganisationList => this.sortingOrganisations(childOrganisationList)),
      )
      .subscribe(childOrganisationList =>
        this.childOrganisationList$.next(
          childOrganisationList.map(organisation => this.normalizeOrganisationDataForUI(organisation)),
        ),
      );
  }

  createOrganisation(data: AddOrganisation): Observable<Id> {
    return this.organisationService.addOrganisation(data).pipe(
      catchError(errorMessage => {
        this.alertService.error(errorMessage);
        return throwError(() => errorMessage);
      }),
    );
  }

  // TODO: возможно лучше добавить метод загрузки фото к организации
  editOrganisation(data: OrganisationUpsert): Observable<Id> {
    const orgObs = this.organisationService.editOrganisation(data as EditOrganisation).pipe(
      catchError(errorMessage => {
        this.alertService.error(errorMessage);
        return throwError(() => errorMessage);
      }),
    );

    return orgObs;
  }

  deleteOrganisation(id: string): Observable<Id> {
    return this.organisationService.deleteOrganisation(id).pipe(
      catchError(errorMessage => {
        this.alertService.error(errorMessage, {
          label: this.translateService.instant('common.alerts.errors.error2'),
          autoClose: 0,
        });
        return throwError(() => errorMessage);
      }),
    );
  }

  search(pagination: number, filter: OrganisationSearchParameters): Observable<OrganisationResponseUI> {
    const top = APP_CONFIG.loadingLimit.organisations;
    const start = pagination * APP_CONFIG.loadingLimit.organisations;

    return this.organisationService.searchForOrganisations(filter, top, start).pipe(
      map(response => {
        response.result = response.result?.map(item => this.normalizeOrganisationDataForUI(item));

        return response;
      }),
      catchError(errorMessage => {
        this.alertService.error(errorMessage);
        return throwError(() => errorMessage);
      }),
    );
  }

  resetParentOrganisation(): void {
    this.parentOrganisations$.next([{}]);
  }

  resetChildOrganisationList(): void {
    this.childOrganisationList$.next([{}]);
  }

  resetAll(): void {
    this.resetChildOrganisationList();
  }

  private normalizeOrganisationDataForUI(data: OrganisationUI) {
    data.businessTypesComplected = data.businessTypes?.map(businessType => businessType.name).join(', ');
    return data;
  }

  private sortingOrganisations(organisations: OrganisationView[]): OrganisationView[] {
    const userOrganisationIds = this.userService.authUser?.organisationJobTitles
      ?.map(organisation => organisation.organisationId)
      .filter((value, index, arr) => arr.indexOf(value) === index);

    organisations.sort((org1, org2) => {
      let orgName1 = org1.shortName?.trim().toLowerCase();
      let orgName2 = org2.shortName?.trim().toLowerCase();
      if (!orgName1) return 1;
      if (!orgName2) return -1;

      if (userOrganisationIds?.includes(org1.id)) {
        orgName1 = ` ${orgName1}`;
      }
      if (userOrganisationIds?.includes(org2.id)) {
        orgName2 = ` ${orgName2}`;
      }

      if (orgName1 < orgName2) return -1;
      if (orgName1 > orgName2) return 1;
      return 0;
    });

    return organisations;
  }

  private sortingOrganisationContacts(organisation: OrganisationView): OrganisationView {
    organisation.contacts
      ?.sort((a, b) => a.sortOrder! - b.sortOrder!)
      .map((contact, index) => {
        contact.sortOrder = (index + 1) * 10;
        return contact;
      });

    return organisation;
  }

  private showError(title: string, err: unknown) {
    const message = err instanceof Error ? err.message : err;
    this.alertService.error(`${title}\n${message}`);
  }
}
